import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { fmtDate, fmtHours, money } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import RichTextView from "../components/RichTextView";
import { toast } from "sonner";
import {
  Building2, Calendar, CheckCircle2, Clock, Eraser, FileDown, FileText, IdCard,
  PenLine, Printer, Settings, ThumbsDown, User, Wallet, XCircle,
} from "lucide-react";

// Redesenho da página pública de assinatura (delta 2026-09-16, referência
// visual fornecida) — página SEM shell do LOOTUS (sem navbar/menu lateral/
// usuário logado): é acessada pelo cliente do cliente através do link
// público, nunca por alguém autenticado no sistema. Só o layout/visual
// muda aqui; token, fluxo de assinatura/rejeição e as chamadas a
// /api/public/sign/... permanecem exatamente como já homologados.
function InfoCard({ Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={15} className="text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] font-medium tracking-wide text-slate-400 uppercase">{label}</div>
        <div className="text-sm font-semibold text-slate-900 truncate">{value}</div>
      </div>
    </div>
  );
}

// Public page — no auth needed
export default function Sign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [signerName, setSignerName] = useState("");
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  // Trava síncrona contra clique duplo: um ref muda de valor imediatamente,
  // antes de qualquer re-render — diferente do estado `busy`, que só
  // desabilita o botão no DOM depois que o React confirma a atualização.
  // Sem isso, dois eventos de clique muito próximos (duplo clique, duplo
  // toque) podem escapar do guard de `disabled` e disparar duas requisições
  // para a mesma assinatura.
  const submittingRef = useRef(false);

  useEffect(() => {
    // don't use api (adds auth) — direct axios-less fetch to /api/public
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL + "/api";
        const r = await fetch(`${base}/public/sign/${token}`);
        if (!r.ok) throw new Error("Link inválido ou expirado");
        const d = await r.json();
        setData(d);
        if (d.already_signed) setDone(true);
        if (d.already_rejected) setRejected(true);
      } catch (e) { setErr(e.message); }
    })();
  }, [token]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * dpr;
    c.height = c.offsetHeight * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = "#0F172A";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctxRef.current = ctx;
  }, [data]);

  const getPoint = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); setDrawing(true); const p = getPoint(e); ctxRef.current.beginPath(); ctxRef.current.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing) return; e.preventDefault(); const p = getPoint(e); ctxRef.current.lineTo(p.x, p.y); ctxRef.current.stroke(); setHasDrawn(true); };
  const end = () => setDrawing(false);
  const clear = () => {
    const c = canvasRef.current;
    ctxRef.current.clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  const submit = async () => {
    if (submittingRef.current) return;
    if (!signerName.trim()) return toast.error("Informe seu nome");
    if (!hasDrawn) return toast.error("Faça sua assinatura");
    submittingRef.current = true;
    setBusy(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const base = process.env.REACT_APP_BACKEND_URL + "/api";
      const r = await fetch(`${base}/public/sign/${token}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signer_name: signerName, signature_data: dataUrl }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Falha ao enviar assinatura");
      }
      setDone(true); toast.success("Assinatura registrada. Obrigado!");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); submittingRef.current = false; }
  };

  const submitReject = async () => {
    if (!signerName.trim()) return toast.error("Informe seu nome");
    if (!rejectReason.trim()) return toast.error("Informe o motivo");
    setBusy(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL + "/api";
      const r = await fetch(`${base}/public/sign/${token}/reject`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signer_name: signerName, reason: rejectReason }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Falha ao enviar"); }
      setRejected(true); toast.success("Rejeição registrada. O analista será notificado.");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  // "Salvar em PDF" (item 3) — reaproveita o MESMO gerador de PDF já
  // usado pela ficha autenticada (server.py: _build_ticket_pdf_response),
  // exposto aqui por uma rota pública própria (GET /public/sign/{token}/pdf)
  // que localiza a ficha exclusivamente pelo token — nunca um id de ficha
  // nem company_id vindos do navegador. Sem Authorization (página pública,
  // igual ao restante desta tela).
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL + "/api";
      const r = await fetch(`${base}/public/sign/${token}/pdf`);
      if (!r.ok) throw new Error("Falha ao gerar PDF");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `ficha_${data?.ticket?.numero || token}.pdf`;
      a.click();
    } catch (e) { toast.error(e.message || "Falha ao gerar PDF"); }
    finally { setDownloadingPdf(false); }
  };

  // "Imprimir" (item 4) — impressão nativa do navegador; o CSS de
  // impressão (classes print:hidden abaixo) cuida de esconder botões e o
  // formulário de assinatura ainda não preenchido.
  const printPage = () => window.print();

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md bg-white rounded-xl border border-red-200 p-8 shadow-sm">
        <h1 className="font-heading text-xl font-bold text-red-700">Link inválido</h1>
        <p className="text-slate-600 text-sm mt-2">{err}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando...</div>;
  const t = data.ticket;

  const empresaNome = data.empresa?.nome || "Relatório de Serviços Prestados";
  const statusBadge = done
    ? { label: "Assinada", cls: "bg-emerald-100 text-emerald-800" }
    : rejected
      ? { label: "Rejeitada pelo cliente", cls: "bg-red-100 text-red-800" }
      : { label: "Aguardando assinatura", cls: "bg-cyan-100 text-cyan-800" };

  return (
    // Página pública — SEM navbar/menu/usuário do LOOTUS (item 1): o
    // único "shell" é este container próprio. max-w-4xl (antes 3xl) —
    // "mais larga", conforme referência visual.
    <div className="min-h-screen bg-slate-50 py-8 px-4 print:bg-white print:py-0">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm print:shadow-none print:border-none">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-blue-600 text-white flex items-center justify-center font-heading font-bold shrink-0">
                {empresaNome.trim().charAt(0).toUpperCase() || "R"}
              </div>
              <div>
                <div className="font-heading font-bold text-slate-900">{empresaNome}</div>
                <div className="text-xs text-slate-500">Assinatura da ficha de serviço</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={statusBadge.cls} data-testid="sign-status-badge">{statusBadge.label}</Badge>
              <div className="flex items-center gap-2 print:hidden">
                <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloadingPdf} data-testid="sign-download-pdf">
                  <FileDown size={15} /> {downloadingPdf ? "Gerando..." : "Salvar em PDF"}
                </Button>
                <Button variant="outline" size="sm" onClick={printPage} data-testid="sign-print">
                  <Printer size={15} /> Imprimir
                </Button>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div className="text-xs text-slate-400 mb-3">Ficha nº {t.numero} • {fmtDate(t.data_servico)}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-4">
              <InfoCard Icon={FileText} label="Nº da ficha" value={`#${t.numero}`} />
              <InfoCard Icon={Calendar} label="Data" value={fmtDate(t.data_servico)} />
              <InfoCard Icon={Settings} label="Serviço" value={t.tipo_servico} />
              <InfoCard Icon={User} label="Analista" value={data.analista?.name || "-"} />
              <InfoCard Icon={Building2} label="Cliente" value={data.cliente?.razao_social || "-"} />
              <InfoCard Icon={IdCard} label="CPF/CNPJ" value={data.cliente?.cpf_cnpj || "-"} />
              <InfoCard Icon={Clock} label="Horário" value={`${t.hora_inicio} – ${t.hora_fim}`} />
              <InfoCard Icon={Clock} label="Horas" value={fmtHours(t.horas_realizadas)} />
            </div>
            {/* O valor é o mesmo `valor_total` que o LOOTUS leva ao faturamento —
                vale para cobrança por hora, por diária e para o valor fechado,
                porque o cálculo já foi resolvido na ficha. Fichas antigas sem
                valor não mostram o bloco, em vez de exibir R$ 0,00. */}
            {Number(t.valor_total) > 0 && (
              <div className="mt-4 flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"
                   data-testid="sign-valor">
                <Wallet size={17} className="text-slate-400 shrink-0" />
                <div>
                  <div className="text-[11px] font-medium tracking-wide text-slate-400 uppercase">Valor</div>
                  <div className="text-base font-semibold text-slate-900">{money(t.valor_total)}</div>
                </div>
              </div>
            )}
            <div className="mt-5">
              <div className="text-[11px] font-medium tracking-wide text-slate-400 uppercase">Descrição / Histórico do Serviço</div>
              <RichTextView html={t.descricao} className="text-sm mt-1 text-slate-700" emptyText="-" />
            </div>
            {t.pendencias && <div className="mt-3"><div className="text-[11px] font-medium tracking-wide text-slate-400 uppercase">Pendências</div><div className="text-sm mt-1 text-slate-700 whitespace-pre-wrap">{t.pendencias}</div></div>}
            {t.proximos_passos && <div className="mt-3"><div className="text-[11px] font-medium tracking-wide text-slate-400 uppercase">Próximos passos</div><div className="text-sm mt-1 text-slate-700 whitespace-pre-wrap">{t.proximos_passos}</div></div>}
          </div>
        </div>

        {done ? (
          <div className="bg-white rounded-xl border border-emerald-200 p-6 shadow-sm text-center" data-testid="sign-done">
            <CheckCircle2 className="mx-auto text-emerald-600" size={48} />
            <h2 className="font-heading text-2xl font-bold text-slate-900 mt-3">Ficha assinada!</h2>
            <p className="text-slate-600 mt-2">
              {t.signer_name ? `Assinado por ${t.signer_name}` : "Assinatura registrada"}
              {t.signed_at && ` em ${new Date(t.signed_at).toLocaleString("pt-BR")}`}
            </p>
          </div>
        ) : rejected ? (
          <div className="bg-white rounded-xl border border-red-200 p-6 shadow-sm text-center" data-testid="sign-rejected">
            <XCircle className="mx-auto text-red-600" size={48} />
            <h2 className="font-heading text-2xl font-bold text-slate-900 mt-3">Atendimento rejeitado</h2>
            <p className="text-slate-600 mt-2">O analista foi notificado e vai revisar sua manifestação. Obrigado.</p>
            {t.signer_rejected_reason && (
              <div className="mt-3 text-sm text-slate-700 bg-slate-50 rounded-md p-3 text-left" data-testid="sign-rejected-reason">
                <b>Motivo da rejeição:</b><br />{t.signer_rejected_reason}
                {t.signer_rejected_at && <div className="text-xs text-slate-500 mt-1">Registrado em {new Date(t.signer_rejected_at).toLocaleString("pt-BR")}</div>}
              </div>
            )}
          </div>
        ) : rejectMode ? (
          <div className="bg-white rounded-xl border border-red-200 p-6 shadow-sm print:hidden">
            <div className="flex items-center gap-2 mb-3">
              <ThumbsDown size={18} className="text-red-600" />
              <div className="font-heading font-semibold">Não concordo com o atendimento</div>
            </div>
            <p className="text-xs text-slate-500 mb-4">Descreva abaixo os pontos que precisam de correção. O analista receberá um e-mail com sua observação.</p>
            <div className="space-y-3">
              <div><Label>Seu nome</Label><Input value={signerName} onChange={e => setSignerName(e.target.value)} data-testid="reject-name" /></div>
              <div><Label>Motivo / pontos a corrigir</Label><Textarea rows={4} value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-testid="reject-reason-input" placeholder="Ex.: as horas descritas não conferem com o horário do atendimento..." /></div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setRejectMode(false)} className="flex-1">Voltar</Button>
                <Button onClick={submitReject} disabled={busy} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="submit-reject"><ThumbsDown size={16} /> {busy ? "Enviando..." : "Enviar rejeição"}</Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm print:hidden">
            <div className="flex items-center gap-2 mb-3">
              <PenLine size={18} className="text-blue-600" />
              <div className="font-heading font-semibold">Confirme e assine abaixo</div>
            </div>
            <p className="text-xs text-slate-500 mb-4">Ao assinar, você confirma que o atendimento acima foi realizado conforme descrito.</p>
            <div className="space-y-3">
              <div>
                <Label>Nome completo</Label>
                <Input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Como consta no documento" data-testid="signer-name" />
              </div>
              <div>
                <Label>Assinatura (desenhe abaixo)</Label>
                <canvas
                  ref={canvasRef}
                  className="w-full h-52 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 cursor-crosshair touch-none"
                  onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                  onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                  data-testid="sign-canvas"
                />
                <div className="mt-2 flex justify-between items-center">
                  <Button type="button" variant="ghost" size="sm" onClick={clear}><Eraser size={14} /> Limpar</Button>
                  <span className="text-xs text-slate-400">{hasDrawn ? "Assinatura capturada" : "Aguardando..."}</span>
                </div>
              </div>
              <Button onClick={submit} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="sign-submit">
                <CheckCircle2 size={16} /> {busy ? "Enviando..." : "Confirmar e assinar"}
              </Button>
              <div className="text-center pt-2 border-t border-slate-100">
                <button type="button" className="text-sm text-red-600 hover:text-red-700 hover:underline inline-flex items-center gap-1" onClick={() => setRejectMode(true)} data-testid="open-reject">
                  <ThumbsDown size={14} /> Não concordo com o atendimento
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="text-center text-xs text-slate-400">© {new Date().getFullYear()} · {empresaNome}</div>
      </div>
    </div>
  );
}
